// @ts-strict-ignore
import * as React from 'react';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { ThunderboltFilled } from '@ant-design/icons';
import Progress from 'antd/lib/progress';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { AppleAppStorePhasedReleaseState, AutomationAutoStagedRollout, PhasedRelease } from '../../types';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { PausedByBlackoutBadge } from '../monitoring/PhasedReleaseCard';
import { getRolloutDaysToPercentageFromAutomation } from '@/helpers';
import { selectAutomationById } from '@/selectors/selectors';
import { Nullable } from '@/typescriptHelpers';

interface PhasedReleaseProgressProps extends PhasedRelease {
    showPercent?: boolean;
    appId: string;
    progressStyle?: React.CSSProperties;
    phasedReleasePaused?: boolean;
    isAutoAccelerated?: boolean;
    isAutoHalted?: boolean;
    autoStagedRolloutAutomationId: Nullable<string>;
}

const iconForPhasedReleaseProgressAndDay = (phasedReleaseProgress: PhasedReleaseProgressProps, dayNumber: number) => {
    if (phasedReleaseProgress.dayNumber === dayNumber) {
        if (phasedReleaseProgress.isAutoAccelerated || phasedReleaseProgress.isAutoHalted) {
            return <ThunderboltFilled style={{ color: runwayColors.primary }} />;
        } else if (phasedReleaseProgress.isOnBlackoutPeriod && !!phasedReleaseProgress.blackoutEndsAt) {
            return <PausedByBlackoutBadge phasedRelease={phasedReleaseProgress} isSizeSmall />;
        }
    }

    return <div style={{ minWidth: 14, height: 14 }} />;
};

const percentLabel = (percent: number, index: number) => {
    const truncated = Math.trunc(percent * 100) / 100;
    const ellipsis = truncated === percent ? '' : '...';
    return index === 0 ? `${truncated}${ellipsis}%` : `${truncated}${ellipsis}`;
};

export const PhasedReleaseProgress: React.FC<PhasedReleaseProgressProps> = (props: PhasedReleaseProgressProps) => {
    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, props.autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const daysToPercent = getRolloutDaysToPercentageFromAutomation(autoStagedRolloutAutomation);

    const customDays = daysToPercent
        ?.map((percent, index) => [index + 1, percent])
        .slice()
        .reverse() as [number, number][];

    const dayPairs: [number, number][] =
        customDays ?? [7, 6, 5, 4, 3, 2, 1].map((day) => [day, applePhasedReleasePercentByDay(day)]);

    const percentLabelChars = Math.max(
        '100%'.length,
        ...dayPairs.map(([, percent], index) => percentLabel(percent, index).length),
    );
    const dayLabelChars = Math.max(...dayPairs.map(([dayNumber]) => `Day ${dayNumber}`.length));

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: 330,
                position: 'relative',
            }}
        >
            {dayPairs.map(([dayNumber, phasedPercentByDay], index) => {
                let phasedPercent = phasedPercentByDay;
                const isCompletedDay = dayNumber <= props.dayNumber;
                const isRolloutAccelerated =
                    props.isManuallyAcceleratedPhasedRollout || props.isRunwayAcceleratedRollout;

                if (
                    props.dayNumber === dayNumber &&
                    isRolloutAccelerated &&
                    props.state === AppleAppStorePhasedReleaseState.Complete
                ) {
                    // accelerated to 100% on this day
                    phasedPercent = 100;
                }

                let strokeColor = antGrey.grey6;

                if (isCompletedDay) {
                    strokeColor = runwayColors.primary;
                    if (props.dayNumber === dayNumber) {
                        if (props.phasedReleasePaused || props.isAutoHalted) {
                            strokeColor = runwayColors.statusRed;
                        } else if (props.isAutoAccelerated) {
                            strokeColor = runwayColors.statusGreen;
                        }
                    }
                }

                const isSkippedDay =
                    props.dayNumber < dayNumber &&
                    isRolloutAccelerated &&
                    props.state === AppleAppStorePhasedReleaseState.Complete;

                if (isSkippedDay) {
                    phasedPercent = 100;
                    strokeColor = antGrey.grey6;
                }

                return (
                    <div
                        key={dayNumber}
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        {iconForPhasedReleaseProgressAndDay(props, dayNumber)}
                        <Tooltip placement="right" title={`${phasedPercent}%`}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    height: 16,
                                    flexGrow: 1,
                                }}
                            >
                                <Text
                                    style={{
                                        color: antGrey.grey7,
                                        fontSize: 10,
                                        lineHeight: '10px',
                                        textTransform: 'uppercase',
                                        marginLeft: 8,
                                        letterSpacing: '0.1em',
                                        flexShrink: 0,
                                        whiteSpace: 'nowrap',
                                        width: `calc(${dayLabelChars}ch + ${dayLabelChars * 0.1}em)`,
                                    }}
                                >
                                    Day {dayNumber}
                                </Text>
                                <Progress
                                    size="small"
                                    percent={phasedPercent}
                                    trailColor={antGrey.grey5}
                                    strokeColor={strokeColor}
                                    style={{ ...props.progressStyle, flex: 1, minWidth: 0 }}
                                    showInfo={false}
                                    status={'normal'}
                                />
                                {props.showPercent !== false && (
                                    <Text
                                        style={{
                                            color: antGrey.grey7,
                                            fontSize: 10,
                                            lineHeight: '10px',
                                            textTransform: 'uppercase',
                                            marginRight: 8,
                                            letterSpacing: '0.1em',
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0,
                                            fontVariantNumeric: 'tabular-nums',
                                            width: `calc(${percentLabelChars}ch + ${percentLabelChars * 0.1}em)`,
                                        }}
                                    >
                                        {percentLabel(phasedPercent, index)}
                                    </Text>
                                )}
                            </div>
                        </Tooltip>
                    </div>
                );
            })}
        </div>
    );
};

export const applePhasedReleasePercentByDay = (dayNumber: number) => {
    if (dayNumber === 0) {
        return 0;
    } else if (dayNumber === 1) {
        return 1;
    } else if (dayNumber === 2) {
        return 2;
    } else if (dayNumber === 3) {
        return 5;
    } else if (dayNumber === 4) {
        return 10;
    } else if (dayNumber === 5) {
        return 20;
    } else if (dayNumber === 6) {
        return 50;
    } else if (dayNumber === 7) {
        return 100;
    } else {
        return 100;
    }
};
